// ─────────────────────────────────────────────
// Shared types for AI Portfolio Manager
// Used by both API Worker and Consumer Worker
// ─────────────────────────────────────────────

export type ImportSource = 'binance' | 'trading212' | 'api_pull';

/** Portfolio display currencies (used across frontend, api-worker, consumer-worker). */
export const SUPPORTED_DISPLAY_CURRENCIES = ['PLN', 'USD', 'EUR', 'GBP'] as const;
export type SupportedDisplayCurrency = (typeof SUPPORTED_DISPLAY_CURRENCIES)[number];

export type ImportStatus = 'pending' | 'uploading' | 'queued' | 'processing' | 'done' | 'error';

export type EventType =
  | 'equity_buy'
  | 'equity_sell'
  | 'dividend'
  | 'crypto_buy'
  | 'crypto_sell_fiat' // taxable: crypto → PLN/EUR/USD
  | 'crypto_swap' // crypto → crypto: tax-neutral (Art. 17 ust. 1f)
  | 'skip';

export type PitBox =
  | 'pos22_revenue' // Part C – przychód (equities)
  | 'pos23_cost' // Part C – koszty (equities)
  | 'pos36_revenue' // Part E – przychód (crypto)
  | 'pos37_cost' // Part E – koszty (crypto)
  | null;

// ─────────────────────────────────────────────
// API request / response shapes
// ─────────────────────────────────────────────

export interface CreateImportJobRequest {
  taxYear: number; // 2023 | 2024 | ...
  source: ImportSource;
}

export interface ImportJob {
  id: string;
  userId: string;
  taxYear: number;
  source: ImportSource;
  status: ImportStatus;
  r2Key?: string;
  errorMsg?: string;
  rowCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface UploadUrlResponse {
  uploadUrl: string; // presigned R2 PUT URL
  r2Key: string;
}

export interface CommitResponse {
  queued: true;
}

export interface ImportStatusResponse {
  id: string;
  status: ImportStatus;
  rowCount?: number;
  errorMsg?: string;
  updatedAt: string;
}

/** Shape emitted on each SSE `status` event for an import job. */
export interface ImportJobSSEEvent {
  status: ImportStatus;
  rowCount?: number;
  errorMsg?: string;
  updatedAt: string;
}

export type SyncJobStatus = 'queued' | 'processing' | 'done' | 'error';

/** Shape emitted on each SSE `status` event for a sync job. */
export interface SyncJobSSEEvent {
  status: SyncJobStatus;
  rowsProcessed?: number;
  errorMsg?: string;
  completedAt?: string;
}

export interface TaxTotals {
  pos22Revenue: number; // Part C revenue
  pos23Cost: number; // Part C cost
  pos36Revenue: number; // Part E revenue (crypto)
  pos37Cost: number; // Part E cost (crypto)
  pos38Income: number; // MAX(0, pos36 - pos37)
  pos39Loss: number; // MAX(0, pos37 - pos36)
}

export interface ImportResultsResponse extends TaxTotals {
  jobId: string;
  taxYear: number;
  source: ImportSource;
  rowCount: number;
  computedAt: string;
}

// ─────────────────────────────────────────────
// Queue message
// ─────────────────────────────────────────────

export interface ProcessJobMessage {
  jobId: string;
  userId: string;
  r2Key: string;
  source: ImportSource;
  taxYear: number;
}

/** Queue message for async API-pull tax calculation (no CSV). */
export interface TaxJobMessage {
  type: 'tax_calc';
  jobId: string;
  userId: string;
  userExchangeId: string;
  exchangeSlug: string;
  taxYear: number;
}

/** Single item in the POST /api/tax/jobs response. */
export interface TaxJobItem {
  jobId: string;
  exchangeSlug: string;
  exchangeName: string;
}

/** Response from POST /api/tax/jobs */
export interface TaxJobsResponse {
  taxYear: number;
  jobs: TaxJobItem[];
}

// ─────────────────────────────────────────────
// Internal: normalised event (after parsing)
// ─────────────────────────────────────────────

export interface NormalisedEvent {
  rowIndex: number;
  rawSnapshot: Record<string, string>;
  eventType: EventType;
  eventDate: string | null; // YYYY-MM-DD
  asset: string | null;
  quantity: number | null;
  priceOrig: number | null;
  currencyOrig: string | null;
  amountOrig: number | null;
  skipReason?: string;
}

// ─────────────────────────────────────────────
// Audit row (after FX enrichment)
// ─────────────────────────────────────────────

export interface AuditRow extends NormalisedEvent {
  id: string;
  jobId: string;
  fxDate: string | null;
  fxRate: number | null;
  amountPln: number | null;
  pitBox: PitBox;
}

// ─────────────────────────────────────────────
// API-based tax calculation (no CSV import)
// ─────────────────────────────────────────────

/** Single trade event enriched with FX rate — returned in the tax calculation API response. */
export interface TaxAuditEntry {
  eventType: string;
  eventDate: string;
  asset: string;
  quantity: number;
  priceOrig: number;
  currencyOrig: string;
  amountOrig: number;
  fxRate: number | null;
  amountPln: number | null;
  pitBox: PitBox;
  exchangeSlug: string;
  exchangeName: string;
}

/** Response from GET /api/tax/calculate?taxYear=YYYY */
export interface TaxCalculationResult {
  taxYear: number;
  /** PIT-38 Part C — equities */
  pos22Revenue: number;
  pos23Cost: number;
  /** PIT-38 Part E — crypto */
  pos36Revenue: number;
  pos37Cost: number;
  /** Derived: MAX(0, pos36 - pos37) */
  pos38Income: number;
  /** Derived: MAX(0, pos37 - pos36) */
  pos39Loss: number;
  /** Number of trade events processed */
  tradeCount: number;
  /** Per-trade detail for audit trail */
  entries: TaxAuditEntry[];
}

// ─────────────────────────────────────────────
// Drizzle DB — schema, factory, types
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// Exchange / User Exchange types
// ─────────────────────────────────────────────

export type SyncStatus = 'never' | 'syncing' | 'ok' | 'error';
export type AssetType = 'crypto' | 'equity' | 'etf' | 'cash';

export interface Exchange {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string;
  apiDocUrl?: string;
  requiredCredentials: string[]; // parsed from JSON
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserExchange {
  id: string;
  userId: string;
  exchangeId: string;
  exchangeSlug?: string; // joined from exchanges table
  exchangeName?: string; // joined from exchanges table
  label?: string;
  lastSyncedAt?: string;
  syncStatus: SyncStatus;
  syncErrorMsg?: string;
  createdAt: string;
  updatedAt: string;
  // NOTE: credentials are stored in Workers KV, never returned in API responses
}

export interface PortfolioPosition {
  id: string;
  userId: string;
  userExchangeId: string;
  assetSymbol: string;
  assetType: AssetType;
  quantity: number;
  avgBuyPrice?: number;
  currency: string;
  lastPrice?: number;
  lastPriceAt?: string;
  updatedAt: string;
  /** Display currency resolved from the user's profile on the server. */
  displayCurrency: string;
  /** qty × lastPrice converted to displayCurrency. Undefined if no FX rate cached yet. */
  valueInCurrency?: number;
  /** qty × avgBuyPrice converted to displayCurrency. Undefined if no avgBuyPrice or no rate. */
  costInCurrency?: number;
}

export interface PortfolioSummary {
  totalPositions: number;
  byAssetType: Record<string, number>;
  /** Total current value of all positions in the user's preferred display currency. */
  totalValueInCurrency: number;
  /** Total cost basis of all positions in the user's preferred display currency. */
  totalCostInCurrency: number;
  displayCurrency: string;
}

// ─────────────────────────────────────────────
// Exchange API request / response shapes
// ─────────────────────────────────────────────

// CreateExchangeRequest removed — exchanges are predefined in the system.
// Admins may only toggle isActive.

export interface UpdateExchangeRequest {
  isActive: boolean;
}

export interface ConnectExchangeRequest {
  exchangeId: string;
  label?: string;
  credentials: Record<string, string>; // plain text — encrypted server-side
}

export interface UpdateUserExchangeRequest {
  label?: string;
  credentials?: Record<string, string>;
}

// ─────────────────────────────────────────────
// Sync queue message
// ─────────────────────────────────────────────

export interface SyncJobMessage {
  type: 'sync_exchange';
  userExchangeId: string;
  userId: string;
  syncJobId: string;
}

export { createDb } from './db/index';
export type { DrizzleDb } from './db/index';
export {
  importJobs,
  auditRows,
  taxTotals,
  fxRates,
  exchanges,
  userExchanges,
  syncJobs,
  portfolioPositions,
  userProfiles,
  featureFlags,
} from './db/schema';
export type {
  ImportJobRow,
  NewImportJob,
  AuditRowDb,
  NewAuditRow,
  TaxTotalsRow,
  NewTaxTotals,
  FxRateRow,
  NewFxRate,
  ExchangeRow,
  NewExchange,
  UserExchangeRow,
  NewUserExchange,
  SyncJobRow,
  NewSyncJob,
  PortfolioPositionRow,
  NewPortfolioPosition,
  UserProfileRow,
  NewUserProfile,
  FeatureFlagRow,
  NewFeatureFlag,
} from './db/schema';
export { PREDEFINED_EXCHANGES } from './predefinedExchanges';
export type { PredefinedExchange } from './predefinedExchanges';
export {
  isValidCurrencyCode,
  getCurrencyName,
  allCurrencyCodes,
  SUPPORTED_CURRENCIES,
} from './currencies';
export type { SupportedCurrency } from './currencies';
